/**
 * Combine screen (combine tool).
 *
 * Flow: 1 Add 2–5 photos (reorderable) → 2 Pick a transition → 3 Video settings
 * (shape + seconds per photo) → Create. The video is rendered on our server (plain
 * video editing, no AI model), so the people in the photos are never changed.
 * The price is data (GET /credits/pricing); nothing here hard-codes it.
 * v1 takes photos only (no video clips) and uses one transition for the whole video.
 */
import { ApiError, api } from '../api/client.js';
import { navigate } from '../router.js';
import { escapeHtml } from '../components/placeholder.js';
import { icon } from '../components/icon.js';
import { toast, toastApiError } from '../ui/toast.js';
import { creditsChanged, getBalance, refreshCredits } from '../state/credits.js';
import { requireAccount, requireSignIn } from '../state/gate.js';
import { withUploadGuide } from '../components/uploadGuide.js';
import type { CreditPricingConfig, JobOptions, TransitionStyle, UploadFileMeta, VideoAspect } from '@loomia/shared';

const TOOL = 'combine' as const;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const CLIENT_MAX_BYTES = 25 * 1024 * 1024;
const MIN_PHOTOS = 2;
const MAX_PHOTOS = 5;

const STYLES: ReadonlyArray<{ id: TransitionStyle; label: string; icon: string }> = [
  { id: 'fade', label: 'Fade', icon: 'blur_on' },
  { id: 'slide', label: 'Slide', icon: 'swipe_left' },
  { id: 'zoom', label: 'Zoom', icon: 'zoom_in' },
  { id: 'spin', label: 'Spin', icon: 'autorenew' },
  { id: 'glitch', label: 'Glitch', icon: 'bolt' },
];
const ASPECTS: readonly VideoAspect[] = ['9:16', '1:1', '16:9'];
const SECONDS: ReadonlyArray<1 | 2 | 3> = [1, 2, 3];

interface Slot {
  key: number;
  status: 'uploading' | 'ready' | 'error';
  progress: number;
  previewUrl: string | null;
  fileId: string | null;
  error: string | null;
}

let renderSeq = 0;
let prevCleanup: (() => void) | null = null;

function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

export function renderCombine(root: HTMLElement, _query: URLSearchParams): void {
  const seq = ++renderSeq;
  const alive = (): boolean => seq === renderSeq && root.isConnected;

  const load = async (): Promise<void> => {
    if (prevCleanup) {
      prevCleanup();
      prevCleanup = null;
    }
    root.innerHTML = '<div class="co"><div class="co-skel"></div></div>';
    const [pricingR] = await Promise.allSettled([api.getPricing(), refreshCredits().catch(() => null)]);
    if (!alive()) return;
    if (pricingR.status === 'rejected' || !pricingR.value.perTool[TOOL]) {
      root.innerHTML = `
        <div class="co"><div class="co-state">${icon('cloud_off')}
          <p>Couldn't load Combine. Check your connection.</p>
          <button class="btn btn-secondary" data-act="retry">Try again</button></div></div>`;
      root.querySelector('[data-act="retry"]')?.addEventListener('click', () => void load());
      return;
    }
    mount(pricingR.value);
  };

  const mount = (pricing: CreditPricingConfig): void => {
    let slots: Slot[] = [];
    let slotSeq = 0;
    let style: TransitionStyle = 'fade';
    let aspect: VideoAspect = '9:16';
    let seconds: 1 | 2 | 3 = 2;
    let generating = false;

    const price = (): number => pricing.perTool[TOOL]?.credits ?? 0;
    const readyCount = (): number => slots.filter((s) => s.status === 'ready').length;
    const missing = (): string | null => {
      if (slots.some((s) => s.status === 'uploading')) return 'Uploading…';
      if (slots.some((s) => s.status === 'error')) return 'Remove the photos that failed';
      if (readyCount() < MIN_PHOTOS) return `Add at least ${MIN_PHOTOS} photos`;
      return null;
    };
    const options = (): JobOptions => ({ transitionStyle: style, clipDurationSec: seconds, aspectRatio: aspect });

    // -- Markup -----------------------------------------------------------

    const step = (n: number, title: string, extra = ''): string =>
      `<div class="co-step"><span class="co-num">${n}</span><h2>${title}</h2>${extra}</div>`;

    const rowHtml = (s: Slot, i: number): string => {
      const label =
        s.status === 'uploading'
          ? `Uploading… ${Math.round(s.progress * 100)}%`
          : s.status === 'error'
            ? escapeHtml(s.error ?? 'Upload failed')
            : 'Ready';
      return `
        <li class="tr-row is-${s.status}" data-key="${s.key}">
          <span class="tr-order">${i + 1}</span>
          <span class="tr-thumb">${s.previewUrl ? `<img src="${escapeHtml(s.previewUrl)}" alt="Photo ${i + 1}" />` : ''}</span>
          <span class="tr-meta"><strong>Photo ${i + 1}</strong><small data-slot-label>${label}</small>${
            s.status === 'uploading' ? `<span class="co-bar tr-bar"><span style="width:${Math.round(s.progress * 100)}%"></span></span>` : ''
          }</span>
          <button class="tr-btn" data-up="${s.key}" aria-label="Move photo ${i + 1} up" ${i === 0 ? 'disabled' : ''}>${icon('arrow_upward')}</button>
          <button class="tr-btn" data-down="${s.key}" aria-label="Move photo ${i + 1} down" ${i === slots.length - 1 ? 'disabled' : ''}>${icon('arrow_downward')}</button>
          <button class="tr-btn" data-remove="${s.key}" aria-label="Remove photo ${i + 1}">${icon('close')}</button>
        </li>`;
    };

    const photosHtml = (): string => {
      if (slots.length === 0) {
        return `
          <button class="co-drop" data-act="pick">
            ${icon('add_photo_alternate')}<strong>Add photos</strong><span>Pick ${MIN_PHOTOS}–${MAX_PHOTOS} · JPG, PNG or WebP · up to 25 MB each</span>
          </button>`;
      }
      const left = MAX_PHOTOS - slots.length;
      return `
        <ol class="tr-list">${slots.map(rowHtml).join('')}</ol>
        ${left > 0 ? `<button class="tr-add" data-act="pick">${icon('add')}<span>Add more (${left} left)</span></button>` : ''}`;
    };

    const stylesHtml = (): string => `
      <div class="co-types co-types-5" role="radiogroup" aria-label="Transition style">
        ${STYLES.map(
          (t) => `
          <button class="co-type${t.id === style ? ' selected' : ''}" role="radio" aria-checked="${t.id === style}" data-style="${t.id}">
            <span class="co-type-ico">${icon(t.icon)}</span><span>${escapeHtml(t.label)}</span>
          </button>`,
        ).join('')}
      </div>`;

    const settingsHtml = (): string => {
      const n = readyCount() || slots.length;
      const total = n * seconds;
      return `
        <p class="co-sub">Shape</p>
        <div class="co-seg" role="radiogroup" aria-label="Video shape">
          ${ASPECTS.map(
            (a) => `<button class="co-seg-opt${a === aspect ? ' selected' : ''}" role="radio" aria-checked="${a === aspect}" data-aspect="${a}">${a}</button>`,
          ).join('')}
        </div>
        <p class="co-sub">Seconds per photo</p>
        <div class="co-seg" role="radiogroup" aria-label="Seconds per photo">
          ${SECONDS.map(
            (d) => `<button class="co-seg-opt${d === seconds ? ' selected' : ''}" role="radio" aria-checked="${d === seconds}" data-seconds="${d}">${d}s</button>`,
          ).join('')}
        </div>
        <p class="co-sub" id="tr-total">${n >= MIN_PHOTOS ? `Your video will be about ${total} seconds (${n} photos × ${seconds}s).` : 'The length depends on how many photos you add.'}</p>`;
    };

    const footerHtml = (): string => {
      const c = price();
      const bal = getBalance();
      const short = bal !== null && bal < c ? c - bal : 0;
      const why = missing();
      const btn =
        short > 0 && !why
          ? '<button class="btn btn-primary btn-block" data-act="buy">Get more credits</button>'
          : `<button class="btn btn-primary btn-block" data-act="generate" ${generating ? 'disabled' : ''}>${
              generating ? 'Starting…' : why ? escapeHtml(why) : `Create Video ${icon('toll', { fill: true })} ${c}`
            }</button>`;
      return `
        <p class="co-sub co-refund">Failed runs are refunded automatically.</p>
        ${short > 0 ? `<p class="co-warn">You need ${icon('toll', { fill: true })} ${short} more credits</p>` : ''}
        ${btn}`;
    };

    const paint = (): void => {
      root.innerHTML = `
        <div class="co">
          <header class="co-head">
            <button class="co-back" data-act="back" aria-label="Back">${icon('arrow_back')}</button>
            <div class="co-head-text"><h1>Combine</h1><p>Join your photos into one smooth video.</p></div>
            <a class="co-credits" href="#/paywall" aria-label="Get credits">${icon('toll', { fill: true })} ${getBalance() ?? '–'} <span>+</span></a>
          </header>
          ${step(1, 'Add photos', `<span class="co-sub">${slots.length}/${MAX_PHOTOS}</span>`)}
          ${photosHtml()}
          <p class="co-note">${icon('info')}Only use photos of yourself or people who agreed.</p>
          ${step(2, 'Choose transition style')}
          ${stylesHtml()}
          ${step(3, 'Video settings')}
          ${settingsHtml()}
          <footer class="co-foot" id="co-foot">${footerHtml()}</footer>
          <input type="file" id="co-file" accept="${ACCEPTED_TYPES.join(',')}" multiple hidden />
        </div>`;
    };

    const paintFooter = (): void => {
      const f = root.querySelector<HTMLElement>('#co-foot');
      if (f) f.innerHTML = footerHtml();
    };

    // -- Upload -----------------------------------------------------------

    const uploadOne = async (file: File): Promise<void> => {
      const slot: Slot = { key: ++slotSeq, status: 'uploading', progress: 0, previewUrl: URL.createObjectURL(file), fileId: null, error: null };
      slots.push(slot);
      paint();
      try {
        const req = await api.requestUpload({ purpose: 'generation_input', contentType: file.type, contentLength: file.size });
        await api.putUploadBytes(req.uploadUrl, file, req.uploadHeaders, (r) => {
          if (!alive() || !slots.includes(slot)) return;
          slot.progress = r;
          const bar = root.querySelector<HTMLElement>(`[data-key="${slot.key}"] .tr-bar span`);
          if (bar) bar.style.width = `${Math.round(r * 100)}%`;
          const label = root.querySelector<HTMLElement>(`[data-key="${slot.key}"] [data-slot-label]`);
          if (label) label.textContent = `Uploading… ${Math.round(r * 100)}%`;
        });
        const meta: UploadFileMeta = await api.completeUpload(req.fileId);
        if (!alive() || !slots.includes(slot)) {
          if (meta.id) void api.deleteUpload(meta.id).catch(() => undefined);
          return;
        }
        if (meta.status !== 'ready') throw new Error(meta.error || 'Image processing failed.');
        slot.status = 'ready';
        slot.fileId = meta.id;
        slot.previewUrl = meta.thumbnails?.thumb256 ?? meta.url ?? slot.previewUrl;
      } catch (err) {
        if (!alive() || !slots.includes(slot)) return;
        slot.status = 'error';
        slot.error = err instanceof Error ? err.message : 'Upload failed.';
        toastApiError(err, 'Upload failed. Please try again.');
      }
      paint();
    };

    const addFiles = (files: File[]): void => {
      if (!requireSignIn('Sign in to add your photos.')) return;
      const room = MAX_PHOTOS - slots.length;
      if (room <= 0) return;
      const picked = files.filter((f) => ACCEPTED_TYPES.includes(f.type) && f.size <= CLIENT_MAX_BYTES);
      if (picked.length < files.length) toast('Some files were skipped. Use JPEG, PNG or WebP under 25 MB.');
      if (picked.length > room) toast(`You can add up to ${MAX_PHOTOS} photos.`);
      for (const f of picked.slice(0, room)) void uploadOne(f);
    };

    const removeSlot = (key: number): void => {
      const s = slots.find((x) => x.key === key);
      if (!s) return;
      slots = slots.filter((x) => x !== s);
      if (s.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(s.previewUrl);
      paint();
      if (s.fileId) void api.deleteUpload(s.fileId).catch(() => undefined);
    };

    const moveSlot = (key: number, by: -1 | 1): void => {
      const i = slots.findIndex((x) => x.key === key);
      const j = i + by;
      if (i < 0 || j < 0 || j >= slots.length) return;
      [slots[i], slots[j]] = [slots[j]!, slots[i]!];
      paint();
    };

    // -- Generate ---------------------------------------------------------

    const generate = async (): Promise<void> => {
      if (generating) return;
      if (!requireAccount('Sign in to generate.')) return;
      const why = missing();
      if (why) {
        toast(why);
        // No photo yet: open the picker right away instead of only saying so.
        if (/^Add /.test(why)) root.querySelector<HTMLElement>('[data-act="pick"]')?.click();
        return;
      }
      const c = price();
      let bal = getBalance();
      if (bal === null) bal = await refreshCredits().catch(() => null);
      if (!alive()) return;
      if (bal !== null && bal < c) {
        navigate('/paywall');
        return;
      }
      generating = true;
      paintFooter();
      try {
        const { job } = await api.submitJob({
          tool: TOOL,
          inputFileIds: slots.filter((s) => s.status === 'ready' && s.fileId).map((s) => s.fileId as string),
          options: options(),
          idempotencyKey: newId(),
        });
        void refreshCredits().catch(() => undefined);
        navigate(`/result/${job.id}`);
      } catch (err) {
        if (!alive()) return;
        if (err instanceof ApiError && (err.status === 402 || /insufficient|credit/i.test(err.code))) {
          toast('Not enough credits.');
          navigate('/paywall');
        } else {
          toastApiError(err, 'Could not start generation. Please try again.');
        }
      } finally {
        if (alive()) {
          generating = false;
          paintFooter();
        }
      }
    };

    // -- Events (delegated) -----------------------------------------------

    root.onclick = (e) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>(
        '[data-act],[data-remove],[data-up],[data-down],[data-style],[data-aspect],[data-seconds]',
      );
      if (!el || (el as HTMLButtonElement).disabled) return;
      const d = el.dataset;
      if (d.remove) removeSlot(Number(d.remove));
      else if (d.up) moveSlot(Number(d.up), -1);
      else if (d.down) moveSlot(Number(d.down), 1);
      else if (d.act === 'back') {
        if (history.length > 1) history.back();
        else navigate('/');
      } else if (d.act === 'pick') {
        if (requireSignIn('Sign in to add your photos.')) withUploadGuide('combine', () => root.querySelector<HTMLInputElement>('#co-file')?.click());
      } else if (d.act === 'generate') void generate();
      else if (d.act === 'buy') navigate('/paywall');
      else if (d.style) {
        style = STYLES.find((x) => x.id === d.style)?.id ?? style;
        paint();
      } else if (d.aspect) {
        aspect = ASPECTS.find((x) => x === d.aspect) ?? aspect;
        paint();
      } else if (d.seconds) {
        seconds = SECONDS.find((x) => x === Number(d.seconds)) ?? seconds;
        paint();
      }
    };

    root.onchange = (e) => {
      const input = e.target as HTMLInputElement;
      if (input.id !== 'co-file') return;
      const files = Array.from(input.files ?? []);
      input.value = '';
      if (files.length) addFiles(files);
    };

    const unsub = creditsChanged.on(() => {
      if (alive()) paintFooter();
    });
    prevCleanup = () => {
      unsub();
      for (const s of slots) if (s.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(s.previewUrl);
    };

    paint();
  };

  void load();
}
